import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { products } from '../../mocks/products';
import VehicleSearch from './components/VehicleSearch';

const formatPrice = (price: number) => price.toLocaleString('ko-KR');

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState('');
  const [showToast, setShowToast] = useState(false);

  const product = products.find((p) => String(p.name) === id);

  useEffect(() => {
    if (product?.colors?.length > 0) {
      setSelectedColor(product.colors[0]);
    }
  }, [product]);

  useEffect(() => {
    if (!product) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://songlimkorea.com';

    document.title = `${product.description} | 자동차 교환부품 - 송림코리아`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `${product.description} - 송림코리아 자동차 교환부품. ${product.fullDescription || '고품질 차량 교환부품을 안정적인 품질로 공급합니다.'}`);

    const existing = document.querySelectorAll('script[data-schema="product"]');
    existing.forEach(s => s.remove());

    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.description,
      "description": product.fullDescription || product.description,
      "sku": product.partNumber,
      "mpn": product.name,
      "url": `${siteUrl}/product/${product.name}`,
      "image": product.image,
      "brand": {
        "@type": "Brand",
        "name": "송림코리아"
      },
      "category": product.category,
      "offers": {
        "@type": "Offer",
        "priceCurrency": "KRW",
        "price": product.price,
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "송림코리아"
        }
      }
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "홈", "item": siteUrl },
        { "@type": "ListItem", "position": 2, "name": "제품 목록", "item": `${siteUrl}/shop` },
        { "@type": "ListItem", "position": 3, "name": product.description, "item": `${siteUrl}/product/${product.name}` }
      ]
    };

    [productSchema, breadcrumbSchema].forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'product');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="product"]').forEach(s => s.remove());
    };
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center px-4">
        <div className="text-center">
          <h1
            className="text-4xl md:text-6xl font-bold text-[#3D2817] mb-6"
            style={{ fontFamily: "'Anton', sans-serif" }}
          >
            제품을 찾을 수 없습니다
          </h1>
          <Link
            to="/shop"
            className="inline-block px-8 md:px-12 py-3 md:py-4 bg-[#3D2817] text-[#F5F1E8] text-base md:text-lg font-bold tracking-widest hover:bg-[#2C1F12] transition-colors duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Anton', sans-serif" }}
          >
            제품 목록으로
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    const existingCart = localStorage.getItem('cart');
    const cart = existingCart ? JSON.parse(existingCart) : [];

    const newItem = {
      id: product.name,
      name: product.description,
      price: product.price,
      quantity,
      color: selectedColor,
      image: product.image,
    };

    const existingItemIndex = cart.findIndex(
      (item: { id: string; color: string }) => item.id === product.name && item.color === selectedColor
    );

    if (existingItemIndex > -1) {
      cart[existingItemIndex].quantity += quantity;
    } else {
      cart.push(newItem);
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      navigate('/cart');
    }, 800);
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      {/* Toast */}
      {showToast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-[#3D2817] text-white px-6 py-3 rounded-lg shadow-xl animate-fade-in">
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-lg" />
            <span className="text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>장바구니에 추가되었습니다</span>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="bg-white py-4 md:py-6">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div
            className="flex items-center gap-2 text-xs md:text-sm"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <Link to="/" className="text-[#6B5744] hover:text-[#3D2817] transition-colors cursor-pointer">
              홈
            </Link>
            <span className="text-[#6B5744]">/</span>
            <Link to="/shop" className="text-[#6B5744] hover:text-[#3D2817] transition-colors cursor-pointer">
              제품
            </Link>
            <span className="text-[#6B5744]">/</span>
            <span className="text-[#3D2817] font-semibold truncate max-w-[150px] md:max-w-none">{product.description}</span>
          </div>
        </div>
      </div>

      {/* Product Section */}
      <section className="py-8 md:py-16 lg:py-20 bg-[#F5F1E8]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
            {/* Images */}
            <div>
              <div className="w-full h-[320px] sm:h-[450px] md:h-[550px] lg:h-[600px] mb-4 md:mb-6 overflow-hidden bg-white shadow-lg rounded-lg">
                <img
                  src={product.images[selectedImage]}
                  alt={product.description}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="grid grid-cols-4 gap-2 md:gap-4">
                {product.images.map((image, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSelectedImage(idx)}
                    className={`w-full h-[70px] sm:h-[90px] md:h-[120px] overflow-hidden bg-white shadow-md rounded-md transition-all duration-300 cursor-pointer ${
                      selectedImage === idx ? 'ring-2 md:ring-3 ring-[#3D2817]' : 'hover:ring-2 hover:ring-[#6B5744]'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.description} ${idx + 1}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              {/* Status Badge */}
              {product.status === '예약주문' && (
                <div className="inline-block px-4 md:px-6 py-2 bg-[#B8860B] mb-4 md:mb-6 rounded-md">
                  <span
                    className="text-white text-xs md:text-sm font-bold tracking-wider"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    예약주문
                  </span>
                </div>
              )}

              {/* Part Code */}
              <div className="mb-2 md:mb-3">
                <span
                  className="text-sm md:text-base text-[#E63946] font-bold tracking-wider"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {product.name}
                </span>
              </div>

              {/* Product Name */}
              <h1
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#3D2817] mb-3 md:mb-4 leading-tight"
                style={{ fontFamily: "'Anton', sans-serif" }}
              >
                {product.description}
              </h1>

              {/* Part Number */}
              <p className="text-xs md:text-sm text-[#6B5744] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                {product.partNumber}
              </p>

              {/* Price */}
              <div
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#E63946] mb-4 md:mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                ₩{formatPrice(product.price)}
              </div>

              {/* Description */}
              <p
                className="text-sm md:text-base text-[#6B5744] mb-6 md:mb-8 leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {product.fullDescription}
              </p>

              {/* Compatible Vehicles */}
              {product.compatibleVehicles && product.compatibleVehicles.length > 0 && (
                <div className="mb-6 md:mb-8">
                  <h3
                    className="text-base md:text-lg font-bold text-[#3D2817] mb-3"
                    style={{ fontFamily: "'Anton', sans-serif" }}
                  >
                    호환 차종
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.compatibleVehicles.map((vehicle, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-[#3D2817]/10 text-[#3D2817] text-xs md:text-sm rounded-full"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {vehicle}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Selection */}
              {product.colors && product.colors.length > 1 && (
                <div className="mb-6 md:mb-8">
                  <h3
                    className="text-base md:text-lg font-bold text-[#3D2817] mb-3"
                    style={{ fontFamily: "'Anton', sans-serif" }}
                  >
                    색상 선택
                  </h3>
                  <div className="flex gap-3">
                    {product.colors.map((color, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedColor(color)}
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-3 transition-all duration-300 cursor-pointer ${
                          selectedColor === color
                            ? 'border-[#3D2817] scale-110'
                            : 'border-[#6B5744]/30 hover:border-[#6B5744] hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        aria-label={`색상 ${idx + 1} 선택`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="mb-6 md:mb-8">
                <h3
                  className="text-base md:text-lg font-bold text-[#3D2817] mb-3"
                  style={{ fontFamily: "'Anton', sans-serif" }}
                >
                  수량
                </h3>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-10 h-10 md:w-12 md:h-12 bg-white border-2 border-[#3D2817] text-[#3D2817] text-lg md:text-xl font-bold hover:bg-[#3D2817] hover:text-white transition-colors duration-300 cursor-pointer rounded-md"
                  >
                    –
                  </button>
                  <span
                    className="text-xl md:text-2xl font-bold text-[#3D2817] w-10 md:w-12 text-center"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => q + 1)}
                    className="w-10 h-10 md:w-12 md:h-12 bg-white border-2 border-[#3D2817] text-[#3D2817] text-lg md:text-xl font-bold hover:bg-[#3D2817] hover:text-white transition-colors duration-300 cursor-pointer rounded-md"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                type="button"
                onClick={handleAddToCart}
                className="w-full px-8 py-4 md:py-5 bg-[#3D2817] text-[#F5F1E8] text-base md:text-lg font-bold tracking-wider hover:bg-[#2C1F12] transition-colors duration-300 mb-6 cursor-pointer whitespace-nowrap rounded-lg"
                style={{ fontFamily: "'Anton', sans-serif" }}
              >
                장바구니 담기
              </button>

              {/* Product Features */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {product.features.map((feature, idx) => (
                  <div key={idx} className="bg-white p-4 md:p-5 shadow-md rounded-lg">
                    <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center mb-2 md:mb-3">
                      <i className={`${feature.icon} text-xl md:text-2xl text-[#3D2817]`} />
                    </div>
                    <h4
                      className="text-sm md:text-base font-bold text-[#3D2817] mb-1"
                      style={{ fontFamily: "'Anton', sans-serif" }}
                    >
                      {feature.title}
                    </h4>
                    <p
                      className="text-xs md:text-sm text-[#6B5744]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Craft Story Section */}
      <section className="py-12 md:py-20 lg:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
            <div className="w-full h-[280px] sm:h-[380px] md:h-[450px] lg:h-[500px] overflow-hidden shadow-xl rounded-lg">
              <img
                src={product.craftStory.image}
                alt="제품 스토리"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div>
              <h2
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#3D2817] mb-4 md:mb-6 leading-tight"
                style={{ fontFamily: "'Anton', sans-serif" }}
              >
                제품 상세 정보
              </h2>
              <p
                className="text-sm md:text-base text-[#6B5744] mb-6 md:mb-8 leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {product.craftStory.story}
              </p>
              <div className="space-y-3 md:space-y-4">
                {product.craftStory.details.map((detail, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0 bg-[#3D2817]/10 rounded-full">
                      <i className="ri-check-line text-base md:text-lg text-[#3D2817]" />
                    </div>
                    <p
                      className="text-sm md:text-base text-[#6B5744] leading-relaxed"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-12 md:py-20 lg:py-24 bg-[#F5F1E8]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#3D2817] mb-8 md:mb-12 text-center"
            style={{ fontFamily: "'Anton', sans-serif" }}
          >
            관련 제품
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {products
              .filter((p) => p.category === product.category && p.name !== product.name)
              .slice(0, 3)
              .map((relatedProduct) => (
                <Link
                  key={relatedProduct.name}
                  to={`/product/${relatedProduct.name}`}
                  className="group cursor-pointer"
                  data-product-shop
                >
                  <div className="w-full h-[180px] sm:h-[250px] md:h-[320px] lg:h-[400px] mb-3 md:mb-4 overflow-hidden bg-white shadow-md rounded-lg">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.description}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>

                  <div className="text-center">
                    <h3
                      className="text-sm sm:text-base md:text-lg font-bold text-[#3D2817] mb-1 md:mb-2 tracking-wider line-clamp-1"
                      style={{ fontFamily: "'Anton', sans-serif" }}
                    >
                      {relatedProduct.description}
                    </h3>
                    <div
                      className="text-base sm:text-lg md:text-xl font-bold text-[#E63946]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      ₩{formatPrice(relatedProduct.price)}
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Vehicle Search */}
      <VehicleSearch />
    </div>
  );
}
